import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Calculator } from './Calculator.jsx'
import { pushSheet, popSheet } from '../sheetStack.js'
import { useIsDesktop } from '../useIsDesktop.js'

const PAD_ANIM_MS = 200

// Input che mostra il valore ma blocca la keyboard nativa: tap → apre il
// numpad custom (bottom-sheet). Il tasto 🖩 del numpad apre la Calculator
// full-screen. DONE chiude e salva il valore corrente nel form.
// Public entry point — splits desktop / mobile to satisfy rules-of-hooks
// (mobile version has many more hooks than the desktop one).
export function AmountInput(props) {
  const isDesktop = useIsDesktop()
  return isDesktop ? <AmountInputDesktop {...props} /> : <AmountInputMobile {...props} />
}

function AmountInputDesktop({ value, onChange, placeholder, className = 'tx-amount-input', autoFocus }) {
  return (
    <input
      type="text"
      inputMode="decimal"
      className={className}
      value={value}
      placeholder={placeholder}
      autoFocus={autoFocus}
      onChange={e => {
        // Accept digits, dot/comma, minus; normalise comma to dot.
        const cleaned = e.target.value.replace(/[^\d.,\-]/g, '').replace(',', '.')
        onChange(cleaned)
      }}
    />
  )
}

function AmountInputMobile({ value, onChange, placeholder, className = 'tx-amount-input', autoFocus }) {
  const [padOpen, setPadOpen]       = useState(false) // logical (drives transform)
  const [padMounted, setPadMounted] = useState(false) // DOM presence (delayed unmount)
  const [calcOpen, setCalcOpen]     = useState(false)
  // First key press after opening replaces the current value (calc-style).
  const freshRef = useRef(true)
  useEffect(() => {
    if (padOpen) freshRef.current = true
  }, [padOpen])

  // Mount on open. On close, delay unmount until slide-out completes.
  useEffect(() => {
    if (padOpen) {
      setPadMounted(true)
      return
    }
    if (padMounted) {
      const t = setTimeout(() => setPadMounted(false), PAD_ANIM_MS)
      return () => clearTimeout(t)
    }
  }, [padOpen, padMounted])

  // Toggle body.has-sheet while the numpad is mounted
  useEffect(() => {
    if (!padMounted) return
    pushSheet()
    return () => popSheet()
  }, [padMounted])

  // Aperto al primo render se autoFocus richiesto (nuovo movimento)
  useEffect(() => {
    if (autoFocus) setPadOpen(true)
  }, [autoFocus])

  // Chiudi su tap esterno (come una keyboard nativa)
  useEffect(() => {
    if (!padOpen) return
    function onPointerDown(e) {
      const np = document.querySelector('.numpad')
      if (np && np.contains(e.target)) return
      // L'input stesso riapre via onClick — non lo escludiamo qui
      setPadOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown, { passive: true })
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [padOpen])

  function press(key) {
    if (key === 'BACK') {
      freshRef.current = false
      onChange(value.slice(0, -1))
      return
    }
    if (key === 'NEG') {
      freshRef.current = false
      if (!value) return
      onChange(value.startsWith('-') ? value.slice(1) : '-' + value)
      return
    }
    // First input after opening: replace the current value
    if (freshRef.current) {
      freshRef.current = false
      if (key === '.')   { onChange('0.'); return }
      if (key === '00')  { onChange('0');  return }
      onChange(key) // digit replaces
      return
    }
    if (key === '.') {
      if (value.includes('.')) return
      onChange((value || '0') + '.')
      return
    }
    if (key === '00') {
      if (!value || value === '0' || value === '-0') return
      onChange(value + '00')
      return
    }
    // digit 0..9
    if (value === '0')      onChange(key)
    else if (value === '-0') onChange('-' + key)
    else                    onChange(value + key)
  }

  function handleCalcResult(result) {
    onChange(result)
    setCalcOpen(false)
  }

  return (
    <>
      <input
        type="text"
        inputMode="none"
        readOnly
        className={`${className}${padOpen ? ' is-active' : ''}`}
        value={value}
        placeholder={placeholder}
        onClick={() => setPadOpen(true)}
        onFocus={() => setPadOpen(true)}
      />
      {padMounted && createPortal(
        <NumPad
          open={padOpen}
          onPress={press}
          onCalc={() => { setPadOpen(false); setCalcOpen(true) }}
          onDone={() => setPadOpen(false)}
        />,
        document.body
      )}
      {calcOpen && createPortal(
        <Calculator
          initial={value}
          onCancel={() => setCalcOpen(false)}
          onDone={handleCalcResult}
        />,
        document.body
      )}
    </>
  )
}

function NumPad({ open, onPress, onCalc, onDone }) {
  // Force a paint at translateY(100%) before transitioning to 0 (slide-in)
  const [entered, setEntered] = useState(false)
  useLayoutEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => setEntered(true))
      return () => cancelAnimationFrame(id)
    }
    setEntered(false)
  }, [open])

  const Btn = ({ k, children, className = '' }) => (
    <button
      type="button"
      className={`np-btn ${className}`}
      onClick={() => (k === 'CALC' ? onCalc() : k === 'DONE' ? onDone() : onPress(k))}
    >
      {children ?? k}
    </button>
  )
  return (
    <div className={`numpad${entered ? ' numpad-entered' : ''}`}>
      <div className="numpad-grid">
        <Btn k="1" /><Btn k="2" /><Btn k="3" />
        <Btn k="BACK" className="np-fn"><BackIcon /></Btn>

        <Btn k="4" /><Btn k="5" /><Btn k="6" />
        <Btn k="NEG" className="np-fn">−</Btn>

        <Btn k="7" /><Btn k="8" /><Btn k="9" />
        <Btn k="CALC" className="np-fn"><CalcIcon /></Btn>

        <Btn k="00" /><Btn k="0" /><Btn k="." />
        <Btn k="DONE" className="np-done">DONE</Btn>
      </div>
    </div>
  )
}

function BackIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 5H9l-7 7 7 7h13a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1z" />
      <line x1="18" y1="9" x2="12" y2="15" />
      <line x1="12" y1="9" x2="18" y2="15" />
    </svg>
  )
}

function CalcIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="8" y1="11" x2="8" y2="11" /><line x1="12" y1="11" x2="12" y2="11" /><line x1="16" y1="11" x2="16" y2="11" />
      <line x1="8" y1="15" x2="8" y2="15" /><line x1="12" y1="15" x2="12" y2="15" /><line x1="16" y1="15" x2="16" y2="15" />
      <line x1="8" y1="19" x2="8" y2="19" /><line x1="12" y1="19" x2="12" y2="19" /><line x1="16" y1="19" x2="16" y2="19" />
    </svg>
  )
}
